<template>
    <div :dusk="props.dusk" class="inline-flex flex-wrap gap-1 select-none">
        <button
            v-for="option in normalizedOptions"
            :key="keyOf(option)"
            type="button"
            :dusk="`${props.dusk}-option-${keyOf(option)}`"
            :disabled="props.disabled"
            class="inline-flex items-center gap-1 px-2 py-0.5 text-xs border rounded cursor-pointer transition hover:opacity-80 disabled:cursor-not-allowed"
            :class="isActive(option) ? '' : 'opacity-40 grayscale'"
            :style="chipStyle(option, isDark)"
            @click="toggle(option)"
        >
            {{ optionLabel(option) }}
        </button>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { FieldComponent } from '@src/Common/Components/types';
import { chipStyle } from '@src/Common/Helpers/chip';
import { useIsDark } from '@src/Common/Composables/useIsDark';

defineOptions({
    inheritAttrs: false,
});

type Selected = null | string | number | object | Array<string | number | object>;

const props = withDefaults(defineProps<FieldComponent>(), {
    modelValue: null,
    options: () => [],
    property: 'id',
    label: 'name',
    multiple: false,
    removable: true,
    disabled: false,
    dusk: null,
});

const emit = defineEmits<{
    (event: 'update:modelValue', content: Selected): void;
    (event: 'change', content: Selected): void;
}>();

const isDark = useIsDark();

const normalizedOptions = computed<any[]>(() => (Array.isArray(props.options) ? props.options : []));

// Stable key for an option (object → its property, scalar → itself).
const keyOf = (item: any): any => (item && typeof item === 'object' ? item[props.property] : item);

const optionLabel = (option: any): any =>
    option && typeof option === 'object' ? (option[props.label] ?? option[props.property]) : option;

const isActive = (option: any): boolean => {
    const value = props.modelValue;

    if (props.multiple) {
        return Array.isArray(value) && value.some((item) => keyOf(item) === option[props.property]);
    }

    if (value === null || typeof value === 'undefined') {
        return false;
    }

    return keyOf(value) === option[props.property];
};

// Multi mode emits the full option objects (like AdminSelect) so callers can normalize to ids.
const toggle = (option: any): void => {
    if (props.disabled) {
        return;
    }

    if (props.multiple) {
        const current = Array.isArray(props.modelValue) ? [...props.modelValue] : [];
        const index = current.findIndex((item) => keyOf(item) === option[props.property]);

        if (index !== -1) {
            current.splice(index, 1);
        } else {
            current.push(option);
        }

        emit('update:modelValue', current);
        emit('change', current);
        return;
    }

    const next = props.removable && isActive(option) ? null : option[props.property];

    emit('update:modelValue', next);
    emit('change', next);
};
</script>
